import type { Plan } from '@projections/engine';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useAppStore } from '../../app/store';
import { type PlanFormValues, planResolver } from '../../forms/planForm';
import { EDITOR_SECTIONS, type EditorSectionId } from './sections';
import { AccountsSection } from './sections/AccountsSection';
import { EventsSection } from './sections/EventsSection';
import { ExpensesSection } from './sections/ExpensesSection';
import { HouseholdSection } from './sections/HouseholdSection';
import { IncomeSection } from './sections/IncomeSection';
import { StrategySection } from './sections/StrategySection';
import './editor.css';

/**
 * PLAN EDITOR (PLAN.md M7 screen 1). A single react-hook-form over the WHOLE engine `Plan`, with a
 * left section nav (ProjectionLab-style). Validation is the SHARED `PlanSchema` via `planResolver`,
 * so every field's inline error is the engine's own message and the plan can never drift from what
 * the simulator accepts.
 *
 * The form is initialised from the active plan in the app store. On valid submit it commits the plan
 * back into the store (so a Run reads it) AND persists it via the active plan store (API when
 * configured, else localStorage). The inner form is KEYED on the plan identity so a startup hydration
 * (which loads a saved plan into the store) remounts it with the right `defaultValues`.
 *
 * Each SECTION component receives the shared `useFormContext`-style handle (passed explicitly here
 * as props for clarity); field arrays (accounts/incomes/expenses/events) use `useFieldArray`.
 */
export function PlanEditorScreen(): React.ReactElement {
  const plan = useAppStore((s) => s.plan);
  const planId = useAppStore((s) => s.planId);
  // Remount the form when the loaded plan changes identity (hydration / loading a different plan),
  // so react-hook-form re-reads `defaultValues`. `planId` is null for the unsaved sample plan.
  return <PlanEditorForm key={planId ?? 'unsaved'} plan={plan} />;
}

function PlanEditorForm({ plan }: { plan: Plan }): React.ReactElement {
  const setPlan = useAppStore((s) => s.setPlan);
  const saveActive = useAppStore((s) => s.saveActive);
  const saveStatus = useAppStore((s) => s.saveStatus);
  const saveError = useAppStore((s) => s.saveError);
  const [active, setActive] = useState<EditorSectionId>('household');

  const form = useForm<PlanFormValues>({
    resolver: planResolver,
    defaultValues: plan,
    mode: 'onBlur',
  });

  // On a VALID submit: commit the edited plan into the store (so a Run reads it) and persist it via
  // the active plan store (API when configured, else localStorage). Status is surfaced inline below.
  const onSubmit: SubmitHandler<PlanFormValues> = (values) => {
    setPlan(values);
    void saveActive();
  };

  return (
    <div className="editor reveal">
      <nav className="editor__sectionnav" aria-label="Plan sections">
        {EDITOR_SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`editor__sectionlink${active === s.id ? ' editor__sectionlink--active' : ''}`}
            onClick={() => setActive(s.id)}
          >
            <span className="editor__sectionlabel">{s.label}</span>
            <span className="editor__sectionblurb">{s.blurb}</span>
          </button>
        ))}
      </nav>

      <form className="editor__panel" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        {active === 'household' && <HouseholdSection form={form} />}
        {active === 'accounts' && <AccountsSection form={form} />}
        {active === 'income' && <IncomeSection form={form} />}
        {active === 'expenses' && <ExpensesSection form={form} />}
        {active === 'events' && <EventsSection form={form} />}
        {active === 'strategy' && <StrategySection form={form} />}

        <div className="editor__footer">
          <button type="submit" className="btn btn--primary" disabled={saveStatus === 'saving'}>
            {saveStatus === 'saving' ? 'Saving…' : 'Save plan'}
          </button>
          {saveStatus === 'saved' && saveError === null && (
            <span className="editor__savehint" aria-live="polite">
              Saved
            </span>
          )}
          {saveError !== null && (
            <span className="editor__saveerror" role="alert">
              {saveError}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
